import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const LOGO = "https://customer-assets.emergentagent.com/job_verify-mesh/artifacts/2steydoe_NeuroMesh%20Logo%20%282%29.png";

const navLinks = [
  { href: '#how-it-works', label: 'Protocol' },
  { href: '#technology', label: 'Technology' },
  { href: '#market', label: 'Market' },
  { href: '#team', label: 'Team' },
  { href: '#roadmap', label: 'Roadmap' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <a href="#" className="flex items-center gap-3 group z-10" data-testid="nav-logo">
          <img src={LOGO} alt="NeuroMesh" className="w-9 h-9 object-contain" />
          <span className="font-display text-white font-semibold text-base tracking-tight">NeuroMesh</span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="text-xs font-medium text-[#888] hover:text-white transition-colors duration-200 uppercase tracking-[0.15em]"
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* CTAs */}
        <div className="hidden md:flex items-center gap-5">
          <a
            href="https://whitepaper.neuro-mesh.io"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#666] hover:text-white transition-colors uppercase tracking-[0.15em]"
            data-testid="nav-whitepaper"
          >
            Whitepaper
          </a>
          <a
            href="https://app.neuro-mesh.io"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-xs px-6 py-2.5"
            data-testid="nav-dapp"
          >
            Open dApp
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-white z-10 p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5 overflow-hidden"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollTo(link.href)}
                  className="block w-full text-left text-sm text-[#888] hover:text-white transition-colors uppercase tracking-[0.12em] py-2"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-4 border-t border-white/10 space-y-3">
                <a href="https://whitepaper.neuro-mesh.io" target="_blank" rel="noopener noreferrer"
                  className="block text-sm text-[#666] hover:text-white uppercase tracking-widest py-2">
                  Whitepaper
                </a>
                <a href="https://app.neuro-mesh.io" target="_blank" rel="noopener noreferrer"
                  className="block border border-[#00F0FF]/60 text-[#00F0FF] text-sm px-6 py-3 text-center uppercase tracking-widest rounded-lg">
                  Open dApp
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
